import React, { useState, useCallback, useEffect } from 'react';
import { Box, Typography, Tooltip, Modal, Button, Slider, IconButton } from '@mui/material';
import { InfoOutlinedIcon, SlideshowOutlinedIcon, GifBoxOutlinedIcon, InsertPhotoOutlinedIcon, CloseIcon } from 'icons/materialIcons';
import Cropper from 'react-easy-crop';
import getCroppedImg from 'utils/images/cropImageUtils';
import { useCardContext } from 'contexts/CardContext';
import { useParams } from 'react-router-dom';
import { getCardById } from 'apis/cardApi';
import { validateUUID } from 'utils/validator';


const CoverPhoto = ({ onCroppedImage, onDeleteCoverPhoto }) => {
  const [image, setImage] = useState(null);
  const [open, setOpen] = useState(false);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [croppedImage, setCroppedImage] = useState(null);
  const [objectUrl, setObjectUrl] = useState(null);
  const { cardState, setCardCoverPhotoUrl } = useCardContext();
  const { cardId } = useParams();
  const [cardDetails, setCardDetails] = useState(null);
  const [coverPhotoUrl, setCoverPhotoUrl] = useState(null);
  const cardCoverPhotoUrl = cardState[cardId]?.cardCoverPhotoUrl;
  const [loading, setLoading] = useState(true);
  const [inputDisabled, setInputDisabled] = useState(false);

  const calculateInitialZoom = useCallback((mediaWidth, mediaHeight) => {
    return 1;
  }, []);

  const onMediaLoaded = useCallback(() => {
    setZoom(1);
    setCrop({ x: 0, y: 0 });
  }, []);

  const handleImageChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      const file = URL.createObjectURL(event.target.files[0]);
      setObjectUrl(file);
      setImage(file);
      setOpen(true);
      event.target.value = '';
    }
  };


  useEffect(() => {
    if (cardDetails?.card && !validateUUID(cardId)) {
      setCoverPhotoUrl(cardDetails.card.coverPhotoUrl || '');
    }
  }, [cardDetails]);



  useEffect(() => {
    if (!validateUUID(cardId)) {
      setCardCoverPhotoUrl(cardId, null);
      return () => {
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
        }
      };
    }
  }, [cardId, objectUrl]);



  useEffect(() => {
    const fetchCardDetails = async () => {
      try {
        const data = await getCardById(cardId);
        setCardDetails(data);
      } catch (error) {
        // Error fetching card details
      }
    };

    if (cardId && !validateUUID(cardId)) {
      fetchCardDetails();
    }
  }, [cardId]);




  useEffect(() => {
    if (cardDetails && !validateUUID(cardId)) {
      setCroppedImage(cardDetails.card.coverPhotoUrl);
    }
  }, [cardDetails]);





  const handleRemoveImage = async () => {
    if (onDeleteCoverPhoto) {
      await onDeleteCoverPhoto();
    }

    setCroppedImage(null);
    setCardCoverPhotoUrl(cardId, null);
    setImage(null);

    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
      setObjectUrl(null);
    }

    setInputDisabled(true);
    setTimeout(() => setInputDisabled(false), 0);
  };



  const handleClose = () => {
    setOpen(false);
    setImage(null);
    setObjectUrl(null);
    setZoom(1);
  };
  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);







  const handleSave = async () => {
    try {
      const croppedImageBlob = await getCroppedImg(image, croppedAreaPixels, 'rectangle', `cover-${cardId}`);

      if (onCroppedImage) {
        onCroppedImage(croppedImageBlob);
      }

      const croppedImageUrl = URL.createObjectURL(croppedImageBlob);
      setCroppedImage(croppedImageUrl);
      setCardCoverPhotoUrl(cardId, croppedImageUrl);
      setOpen(false);
    } catch (e) {
      // Error during image cropping
    }
  };


  return (
    <div>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
        <Typography
          variant="body2"
          sx={{
            fontSize: '0.8rem',
            fontWeight: 600,
            color: '#4f4f4f',
            display: 'flex',
            alignItems: 'center',
            marginBottom: '6px',
          }}
        >
          Cover Photo
          <Tooltip title="Ideal Dimensions: 780 px by 300 px (2.1:1)" arrow>
            <InfoOutlinedIcon sx={{ fontSize: '1rem', marginLeft: 0.5 }} />
          </Tooltip>
        </Typography>


        <Box
          sx={{
            position: 'relative',
            width: 250,
            height: 100,
          }}
        >


          <Box
            sx={{
              width: '100%',
              height: '100%',
              borderRadius: '8px',
              backgroundColor: '#f2f2f2',
              border: '1px solid #dbdbdb',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              textAlign: 'center',
              padding: 1,
              backgroundImage: croppedImage ? `url(${croppedImage})` : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
            component="label"
          >
            {!croppedImage && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, marginBottom: 1 }}>
                  <InsertPhotoOutlinedIcon sx={{ fontSize: '25px', color: '#828282' }} />
                  <SlideshowOutlinedIcon sx={{ fontSize: '25px', color: '#828282' }} />
                  <GifBoxOutlinedIcon sx={{ fontSize: '25px', color: '#828282' }} />
                </Box>
                <Typography
                  variant="body2"
                  sx={{ fontSize: '0.7rem', fontWeight: 400, color: '#212121' }}
                >
                  <span style={{ color: 'blue' }}>Select</span> image, video, gif file or drag and drop one here
                </Typography>
              </>
            )}

            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                top: 0,
                left: 0,
                opacity: 0,
                cursor: 'pointer',
                pointerEvents: croppedImage ? 'none' : 'auto',
              }}
            />
          </Box>

          {croppedImage && (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveImage();
              }}
              sx={{
                position: 'absolute',
                top: 1,
                right: -14,
                color: '#828282',
                backgroundColor: 'rgb(255, 255, 255)',
                '&:hover': { backgroundColor: 'rgb(255, 255, 255)' },
                width: 26,
                height: 26,
                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                zIndex: 2,
              }}
            >
              <CloseIcon sx={{ fontSize: 12 }} />
            </IconButton>
          )}
        </Box>
      </Box>

      <Modal
        open={open}
        onClose={handleClose}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backdropFilter: 'blur(7px)',
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
        }}
      >
        <Box
          sx={{
            outline: 'none',
            width: '90vw',
            maxWidth: '900px',
            height: '90vh',
            maxHeight: '700px',
          }}
        >
          {image && (
            <>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: 'calc(100% - 100px)',
                  marginBottom: 2,
                }}
              >

                <Cropper
                  image={image}
                  crop={crop}
                  zoom={zoom}
                  aspect={2.6}
                  cropShape="rect"
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  onMediaLoaded={onMediaLoaded}
                  objectFit="contain"
                  centerImage={true}
                  showGrid={false}
                  minZoom={0.05}
                  maxZoom={3}
                  restrictPosition={false}
                />

              </Box>

              <Box sx={{ textAlign: 'center' }}>

                <Slider
                  value={zoom}
                  onChange={(e, newValue) => setZoom(newValue)}
                  min={0.05}
                  max={3}
                  step={0.01}
                  defaultValue={1.5}
                  valueLabelDisplay="auto"
                  sx={{ marginBottom: 2 }}
                />
                <Button onClick={handleClose} variant="contained" sx={{ marginRight: 2 }}>
                  Cancel
                </Button>
                <Button onClick={handleSave} variant="contained" color="primary">
                  Crop
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>
    </div>
  );
};

export default CoverPhoto;